// @flow
import React, {
  Component,
  useRef,
  useState,
  useEffect,
  useCallback
} from 'react';
import bem from '@justpark/helpers/src/bem/bem';
import type { Dayjs } from 'dayjs';
import convertToDayjs from '../../../helpers/dates/convertToDayjs';
import areNativeDatePickersSupported from '../../../helpers/areNativeDatePickersSupported';

import '../../../../static/scss/components/nativePicker.scss';

type Props = {|
  value: Dayjs,
  min: Dayjs,
  max: Dayjs,
  onChange: (number, Component<Props, {}>) => void,
  onClick: (Component<Props, {}>) => void,
  className: string,
  name: string,
  children: any,
  shouldRender: boolean,
  showTimePicker?: boolean
|};

const NativeDatePicker = ({
  onClick,
  min,
  max,
  onChange,
  shouldRender,
  className,
  children,
  name,
  value,
  showTimePicker = true
}: Props) => {
  const baseClass = 'c-native-date-picker';
  const dateFormat = 'YYYY-MM-DD';
  const dateTimeFormat = 'YYYY-MM-DDTHH:mm';
  const type = showTimePicker ? 'datetime-local' : 'date';
  const format = showTimePicker ? dateTimeFormat : dateFormat;

  const inputRef = useRef();
  const nativeDatePickersSupported = areNativeDatePickersSupported;

  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => setHasMounted(true), [setHasMounted]);

  const noNative = !hasMounted || !nativeDatePickersSupported || !shouldRender;

  const onClickEvent = useCallback(
    (event: SyntheticEvent<HTMLDivElement>) => {
      event.stopPropagation();
      inputRef.current.click(); // Android hack.
      inputRef.current.focus(); // iOS hack.

      onClick();
    },
    [inputRef, onClick]
  );

  const onChangeEvent = useCallback(
    (event: SyntheticEvent<HTMLInputElement>) => {
      const date = convertToDayjs(event.currentTarget.value);
      let newValue = date.valueOf();

      if (!Number.isNaN(newValue)) {
        if (newValue < parseInt(min, 10)) {
          newValue = min;
        } else if (newValue > parseInt(max, 10)) {
          newValue = max;
        }

        onChange(newValue);
      }
    },
    [max, min, onChange]
  );

  if (noNative) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div className={`${baseClass} ${className}`} onClick={onClickEvent}>
      <input
        className={bem(baseClass, 'input')}
        type={type}
        ref={inputRef}
        value={value.format(format)}
        name={name}
        onChange={onChangeEvent}
        min={min.format('YYYY-MM-DD')}
        max={max.format('YYYY-MM-DD')}
      />
      {children}
    </div>
  );
};

export default NativeDatePicker;
